/* eslint-disable @typescript-eslint/no-explicit-any */
import { LoadableComponent } from '@loadable/component';

export const SERVICE_VIEW_CLASSNAME = 'elc-service-view-renderer';
export const SERVICE_VIEW_RENDERED_ATTRIBUTE = 'data-rendered-view';

export function getModuleFederatedName(serviceName: string) {
    return serviceName.replace(/-/g, '_');
}

export interface ICachedComponentsRegistry {
    [key: string]: React.ComponentType<any>;
}

export type RegisteredComponent = () => Promise<{
    ['default']: React.ComponentType<any>;
}>;

type AnyObject = { [key: string]: any };

export interface IAppConfig extends AnyObject {
    env?: string;
    businessUnitId?: string;
}

export type ServiceInitializer = (config: IAppConfig) => Promise<void> | void;

export interface IViewControllerData {
    [key: string]: any;
}

export type ViewControllerLoadedMethod = (...args: any[]) => Promise<any>;

export interface IComponentViewController {
    id?: string;
    loaded?: ViewControllerLoadedMethod;
    data?: IViewControllerData;
    dataState?: IViewControllerData;
    configMergeStrategy?: Function;
    initialize?(...args: any[]): void;
    handleState?(...args: any[]): void;
    setSSRState(data: IViewControllerData): void;
    setId(id: string): void;
    isLoading: boolean;
}

export interface IComponent {
    componentName: string;
    component: LoadableComponent<any>;
    viewController?(): IComponentViewController;
}

export interface IComponentRegistry {
    [key: string]: IComponent;
}

export interface IFederatedService {
    isStarted: true;
    name: string;
    version: string;
    views: string[];
}

declare class EcommApp {
    isStarted: boolean;
    name: string;
    start(appConfig: any): Promise<void>;
    // @TODO: SDFEFND-4051 Removing this method will require a refactor on Drupal side
    register(): void;
    setupDisabledServices(): Promise<void>;
}

export type IEcommApp = typeof EcommApp;

interface IBaseServiceRegistryComp {
    isStarted: boolean;
    name: string;
    isLazy?: boolean;
    start(appConfig: IAppConfig): Promise<void>;
    startPromise?: Promise<void>;
    version: string;
    views: string[];
}

export type IManifestService = {
    events: string[];
    queries: string[];
};

type BaseServiceRegistryComponent = {
    cachedComponentsRegistry: ICachedComponentsRegistry;
    componentsRegistry: IComponentRegistry;
    getView(componentName: string): IComponent | null;
    getViewFromCache(componentName: string): React.ComponentType<any> | null;
    registerView(
        componentName: string,
        component: RegisteredComponent,
        viewController?: () => IComponentViewController
    ): void;
    serviceInitializer: ServiceInitializer;
    serviceBusController?: any;
    setViewInCache(componentName: string, component: React.ComponentType<any>): void;
};
// Exported object by the createServiceManifest method
export type IManifestServiceRegistryComp = IBaseServiceRegistryComp & IManifestService;
// Service exported by the register method after applying the manifest
export type ILazyServiceRegistryComponent = BaseServiceRegistryComponent &
    IManifestServiceRegistryComp;
// Service exported by the register method if manifest is not applied (non lazy service)
export type IServiceRegistryComponent = BaseServiceRegistryComponent & IBaseServiceRegistryComp;

// Remove after SDFEFND-1853
// Exported from older versions of the register method (elc-service < v7)
export interface IOlderServiceRegistryComponent
    extends Omit<
        IServiceRegistryComponent,
        'getView' | 'start' | 'views' | 'isLazy' | 'startPromise'
    > {
    getView(componentName: string): IComponent | undefined;
    start(appConfig: IAppConfig): Promise<void>;
    // These keys doesn't exist but this will avoid TS errors
    views?: string[];
    isLazy?: boolean;
}

export type AllUpdatedServicesTypes =
    | ILazyServiceRegistryComponent
    | IManifestServiceRegistryComp
    | IServiceRegistryComponent;

export type AllServicesTypes =
    | AllUpdatedServicesTypes
    | IFederatedService
    | IOlderServiceRegistryComponent;

export interface IServiceRegistry {
    [key: string]: AllServicesTypes;
}
